import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeeRoute } from "../../app-routes";
import type { EmployeeLayoutRoute } from "./employee.route";
import { payrollDataQuery } from "./profile.queries";

const STALE_TIME = 30_000;

const employeeName = (context: { app: { getEmployee: (id: number) => { name: string } | undefined } }, id: number) =>
    context.app.getEmployee(id)?.name;

function createCoreDataRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "core-data",
        staticData: {
            title: ({ match, context }) => `Stammdaten: ${employeeName(context, match.params.employeeId)}`,
            menu: employeeRoute("core-data"),
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-core-data-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
            ></ptc-employees-core-data-single>`,
    }).lazy(lazyElement(() => import("./employees-core-data-single")));
}

function createPositionsRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "positions",
        staticData: {
            title: ({ match, context }) => `Positionen: ${employeeName(context, match.params.employeeId)}`,
            menu: employeeRoute("positions"),
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-positions-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
            ></ptc-employees-positions-single>`,
    }).lazy(lazyElement(() => import("./employees-positions-single")));
}

function createPermissionsRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "permissions",
        staticData: {
            title: ({ match, context }) => `Zugänge & Rechte: ${employeeName(context, match.params.employeeId)}`,
            menu: employeeRoute("permissions"),
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-permissions-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
            ></ptc-employees-permissions-single>`,
    }).lazy(lazyElement(() => import("./employees-permissions-single")));
}

function createPayrollDataRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "payroll-data",
        staticData: {
            title: ({ match, context }) => `Lohndaten: ${employeeName(context, match.params.employeeId)}`,
            menu: employeeRoute("payroll-data"),
        },
        loader: ({ params, context: { app, queryClient } }) =>
            queryClient.fetchQuery({ ...payrollDataQuery(app, params.employeeId), staleTime: STALE_TIME }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-payroll-data-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .payrollData=${match.loaderData}
            ></ptc-employees-payroll-data-single>`,
    }).lazy(lazyElement(() => import("./employees-payroll-data-single")));
}

const contractsTitle = ({
    match,
    context,
}: {
    match: { params: { employeeId?: number } };
    context: Parameters<typeof employeeName>[0];
}) => `Verträge: ${employeeName(context, match.params.employeeId!)}`;

const contractPath = (employeeId: number, page: string, contractId?: number) =>
    `/employees/${employeeId}/contracts/${page}${contractId ? `/${contractId}` : ""}`;

type ContractParams = { employeeId: number; contractId?: number };

function parseContractId({ contractId }: { contractId?: string }) {
    return contractId ? { contractId: Number(contractId) } : {};
}

// One component function for all contract routes: the same template keeps the page element (and its unsaved new
// contract) alive while moving between view, edit, new and changes.
function contractsComponent(page: string) {
    return ({ match }: { match: { params: ContractParams } }): TemplateResult =>
        html`<ptc-employees-contracts-single
            class="fullbleed"
            .employeeId=${match.params.employeeId}
            .page=${page}
            .contractId=${match.params.contractId}
        ></ptc-employees-contracts-single>`;
}

const lazyContracts = () => lazyElement(() => import("./employees-contracts-single"));

function createContractsRoutes(parent: EmployeeLayoutRoute) {
    const staticData = { title: contractsTitle } as const;

    // `contracts` and unknown sub-pages open the view; the menu entry lives on `contracts` itself.
    const index = createRoute({
        getParentRoute: () => parent,
        path: "contracts",
        staticData: { ...staticData, menu: employeeRoute("contracts") },
        beforeLoad: ({ params }): void => redirectTo(contractPath(params.employeeId, "view")),
    });
    const unknown = createRoute({
        getParentRoute: () => parent,
        path: "contracts/$",
        staticData,
        beforeLoad: ({ params }): void => redirectTo(contractPath(params.employeeId, "view")),
    });

    const view = createRoute({
        getParentRoute: () => parent,
        path: "contracts/view",
        staticData,
        beforeLoad: ({ params, context }): void => {
            const current = context.app.getEmployee(params.employeeId)?.currentContract;
            if (current) {
                redirectTo(contractPath(params.employeeId, "view", current.id));
            }
        },
        component: contractsComponent("view"),
    }).lazy(lazyContracts());

    const viewContract = createRoute({
        getParentRoute: () => parent,
        path: "contracts/view/$contractId",
        params: {
            parse: parseContractId as never,
            stringify: ({ contractId }: { contractId?: number }) => ({ contractId: String(contractId) }),
        },
        staticData,
        beforeLoad: ({ params, context }): void => {
            const employee = context.app.getEmployee(params.employeeId);
            const exists = employee?.contracts.some((c) => c.id === (params as ContractParams).contractId);
            if (!exists && employee?.currentContract) {
                redirectTo(contractPath(params.employeeId, "view", employee.currentContract.id));
            }
        },
        component: contractsComponent("view"),
    }).lazy(lazyContracts());

    const edit = createRoute({
        getParentRoute: () => parent,
        path: "contracts/edit/$contractId",
        params: {
            parse: parseContractId as never,
            stringify: ({ contractId }: { contractId?: number }) => ({ contractId: String(contractId) }),
        },
        staticData,
        component: contractsComponent("edit"),
    }).lazy(lazyContracts());

    const create = createRoute({
        getParentRoute: () => parent,
        path: "contracts/new",
        staticData,
        component: contractsComponent("new"),
    }).lazy(lazyContracts());

    const changes = createRoute({
        getParentRoute: () => parent,
        path: "contracts/changes",
        staticData,
        component: contractsComponent("changes"),
    }).lazy(lazyContracts());

    return [index, unknown, view, viewContract, edit, create, changes] as const;
}

export function createEmployeeProfileRoutes(parent: EmployeeLayoutRoute) {
    return [
        createCoreDataRoute(parent),
        createPayrollDataRoute(parent),
        createPositionsRoute(parent),
        createPermissionsRoute(parent),
        ...createContractsRoutes(parent),
    ] as const;
}
